import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { ApiService } from '@services/api.service';
import { Observable, tap } from 'rxjs';
import { ApiRequest } from '@enums/api.enum';
import {
	CreateWatchlistItemRequest,
	InvestingSetup,
	MarketRegimeResponse,
	RrCalculatorQuery,
	RrCalculatorResponse,
	UpdateWatchlistItemRequest,
	WatchlistItem,
	WatchlistListResponse,
} from '@models/investing';

@Injectable({
	providedIn: 'root',
})
export class InvestingManagerService {
	private readonly _api = inject(ApiService);

	regime: WritableSignal<MarketRegimeResponse | null> = signal<MarketRegimeResponse | null>(null);
	setupFeed: WritableSignal<InvestingSetup[]> = signal<InvestingSetup[]>([]);
	watchlist: WritableSignal<WatchlistListResponse | null> = signal<WatchlistListResponse | null>(
		null,
	);

	regimeLoading: WritableSignal<boolean> = signal(false);
	feedLoading: WritableSignal<boolean> = signal(false);
	watchlistLoading: WritableSignal<boolean> = signal(false);

	/** Prefill payload for R/R Calculator — set to trigger tab switch */
	calculatorPrefill: WritableSignal<Partial<RrCalculatorQuery> | null> =
		signal<Partial<RrCalculatorQuery> | null>(null);

	/** Unread investing alert count — cleared when user visits /investing */
	unreadInvestingAlerts: WritableSignal<number> = signal(0);

	// ── Regime ──────────────────────────────────────────────────────────────────

	getRegime(): Observable<MarketRegimeResponse> {
		this.regimeLoading.set(true);
		return this._api.sendGET<MarketRegimeResponse>(ApiRequest.InvestingRegime).pipe(
			tap((data) => {
				this.regime.set(data);
				this.regimeLoading.set(false);
			}),
		);
	}

	// ── Setup Feed (user's watchlist top setups) ─────────────────────────────

	getSetupFeed(): Observable<InvestingSetup[]> {
		this.feedLoading.set(true);
		return this._api.sendGET<InvestingSetup[]>(ApiRequest.InvestingSetupFeed).pipe(
			tap((data) => {
				this.setupFeed.set(data);
				this.feedLoading.set(false);
			}),
		);
	}

	// ── Setup Score ──────────────────────────────────────────────────────────

	getSetupScore(symbol: string): Observable<InvestingSetup> {
		return this._api.sendGET<InvestingSetup>(`${ApiRequest.InvestingSetupScore}/${symbol}`);
	}

	// ── Watchlist ────────────────────────────────────────────────────────────

	getWatchlist(): Observable<WatchlistListResponse> {
		this.watchlistLoading.set(true);
		return this._api.sendGET<WatchlistListResponse>(ApiRequest.InvestingWatchlist).pipe(
			tap((data) => {
				this.watchlist.set(data);
				this.watchlistLoading.set(false);
			}),
		);
	}

	addToWatchlist(req: CreateWatchlistItemRequest): Observable<WatchlistItem> {
		return this._api
			.sendPOST<WatchlistItem>(ApiRequest.InvestingWatchlist, req)
			.pipe(tap(() => this.getWatchlist().subscribe()));
	}

	updateWatchlistItem(id: string, req: UpdateWatchlistItemRequest): Observable<WatchlistItem> {
		return this._api
			.sendPUT<WatchlistItem>(`${ApiRequest.InvestingWatchlist}/${id}`, req)
			.pipe(tap(() => this.getWatchlist().subscribe()));
	}

	removeFromWatchlist(id: string): Observable<unknown> {
		return this._api
			.sendDELETE<unknown>(`${ApiRequest.InvestingWatchlist}/${id}`)
			.pipe(tap(() => this.getWatchlist().subscribe()));
	}

	// ── R/R Calculator ───────────────────────────────────────────────────────

	getCalculator(query: RrCalculatorQuery): Observable<RrCalculatorResponse> {
		const params = new URLSearchParams();
		params.set('symbol', query.symbol);
		params.set('entry', query.entry.toString());
		params.set('stop_loss', query.stop_loss.toString());
		params.set('take_profit_1', query.take_profit_1.toString());

		if (query.take_profit_2 != null) {
			params.set('take_profit_2', query.take_profit_2.toString());
		}

		if (query.take_profit_3 != null) {
			params.set('take_profit_3', query.take_profit_3.toString());
		}

		if (query.portfolio_value != null) {
			params.set('portfolio_value', query.portfolio_value.toString());
		}

		if (query.risk_percent != null) {
			params.set('risk_percent', query.risk_percent.toString());
		}

		const url = `${ApiRequest.InvestingCalculator}?${params.toString()}`;
		return this._api.sendGET<RrCalculatorResponse>(url);
	}
}
